import React, { useMemo, useState } from 'react';
import { Card, Table, Button, Tag, Space, Input, Select, DatePicker, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { bidAnalysisService, type BidAnalysis } from '@/services/bidAnalysis.service';

const { RangePicker } = DatePicker;

function namesCell(names?: string[]) {
  if (!names?.length) return '-';
  const label = names.length === 1 ? names[0] : `${names[0]} +${names.length - 1} more`;
  return (
    <Tooltip title={names.join(', ')}>
      <span>{label}</span>
    </Tooltip>
  );
}

const BidAnalysesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [supplierFilter, setSupplierFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { data: analyses = [], isLoading } = useQuery({
    queryKey: ['bid-analyses'],
    queryFn: () => bidAnalysisService.list({}),
  });

  const statusOptions = useMemo(
    () =>
      Array.from(new Set(analyses.map((a) => a.status).filter(Boolean)))
        .sort()
        .map((s) => ({ value: s, label: s })),
    [analyses]
  );

  const supplierOptions = useMemo(() => {
    const names = new Set<string>();
    for (const a of analyses) {
      for (const n of [...(a.quoted_supplier_names ?? []), ...(a.awarded_supplier_names ?? [])]) names.add(n);
    }
    return Array.from(names)
      .sort((x, y) => x.localeCompare(y))
      .map((n) => ({ value: n, label: n }));
  }, [analyses]);

  const filtered = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    const [from, to] = dateRange ?? [null, null];
    return analyses.filter((a) => {
      if (statusFilter && a.status !== statusFilter) return false;
      if (
        supplierFilter &&
        !(a.quoted_supplier_names ?? []).includes(supplierFilter) &&
        !(a.awarded_supplier_names ?? []).includes(supplierFilter)
      ) {
        return false;
      }
      if (from || to) {
        const raw = a.supplierInquiry?.inquiry_date ?? a.created_at;
        if (!raw) return false;
        const d = dayjs(raw);
        if (from && d.isBefore(from, 'day')) return false;
        if (to && d.isAfter(to, 'day')) return false;
      }
      if (!term) return true;
      const haystack = [
        a.analysis_number,
        a.status,
        a.supplierInquiry?.inquiry_number,
        a.recommendation_notes,
        a.approval_notes,
        ...(a.quoted_supplier_names ?? []),
        ...(a.awarded_supplier_names ?? []),
        ...(a.items ?? []).flatMap((i) => [i.item_code, i.item_description]),
      ];
      return haystack.some((v) => v && String(v).toLowerCase().includes(term));
    });
  }, [analyses, searchText, statusFilter, supplierFilter, dateRange]);

  const hasFilters = Boolean(searchText || statusFilter || supplierFilter || dateRange);

  const columns: ColumnsType<BidAnalysis> = [
    {
      title: 'Analysis #',
      dataIndex: 'analysis_number',
      width: 130,
      sorter: (a, b) => String(a.analysis_number ?? '').localeCompare(String(b.analysis_number ?? '')),
    },
    {
      title: 'Inquiry',
      key: 'inquiry',
      width: 140,
      render: (_, r) =>
        r.supplier_inquiry_id ? (
          <Button
            type="link"
            size="small"
            style={{ paddingInline: 0 }}
            onClick={() => navigate(`/scm/procurement/supplier-inquiries/${r.supplier_inquiry_id}`)}
          >
            {r.supplierInquiry?.inquiry_number ?? 'View inquiry'}
          </Button>
        ) : (
          '-'
        ),
    },
    {
      title: 'Inquiry date',
      key: 'inquiryDate',
      width: 110,
      sorter: (a, b) =>
        dayjs(a.supplierInquiry?.inquiry_date ?? 0).valueOf() - dayjs(b.supplierInquiry?.inquiry_date ?? 0).valueOf(),
      render: (_, r) => (r.supplierInquiry?.inquiry_date ? dayjs(r.supplierInquiry.inquiry_date).format('DD MMM YYYY') : '-'),
    },
    { title: 'Quoted suppliers', key: 'quoted', width: 200, render: (_, r) => namesCell(r.quoted_supplier_names) },
    { title: 'Awarded to', key: 'awarded', width: 200, render: (_, r) => namesCell(r.awarded_supplier_names) },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => <Tag color={s === 'Approved' ? 'green' : 'default'}>{s}</Tag>,
    },
    {
      title: 'Actions',
      width: 120,
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/bid-analyses/${row.id}`)}>
          View / Compare
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="Bid analyses">
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search analysis #, inquiry, supplier, item, notes"
            style={{ width: 340 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Select
            placeholder="Supplier"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
            value={supplierFilter}
            onChange={setSupplierFilter}
            options={supplierOptions}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 150 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
          />
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange(v as [Dayjs | null, Dayjs | null] | null)}
            format="DD MMM YYYY"
            allowEmpty={[true, true]}
            placeholder={['Inquiry from', 'Inquiry to']}
          />
          {hasFilters ? (
            <Button
              onClick={() => {
                setSearchText('');
                setStatusFilter(undefined);
                setSupplierFilter(undefined);
                setDateRange(null);
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </Space>
        <Table
          size="small"
          rowKey="id"
          loading={isLoading}
          dataSource={filtered}
          columns={columns}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${total} bid analys${total === 1 ? 'is' : 'es'}`,
          }}
        />
      </Card>
    </div>
  );
};

export default BidAnalysesPage;
